import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import { CaretCard } from '@tcl-argon-foundations/caret-card';
import { InputTheme, TooltipPosition } from '@tcl-argon-models/models';
import { mergeClasses } from '@tcl-argon-utils/utils';
import { useTooltipStyles } from './styles';

type DivElement = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export interface TooltipProps extends DivElement {
  /**
   * An object containing:
   * * a 'base' ReactNode, to be the base of the floating `CaretCard`
   * * a 'tooltip' ReactNode to be displayed inside the floating `CaretCard`
   */
  children: {
    base: React.ReactNode;
    tooltip: React.ReactNode;
  };
  classes?: Record<string, string>;
  /** If true, display a floating `CaretCard` containing the 'tooltip' passeds passed through props.children */
  isOpen?: boolean;
  /** The position of the floating CaretCard relative to the 'base'. */
  position?: TooltipPosition;
  theme?: InputTheme;
}

const tooltipPositionToCaretPosition = (position: TooltipPosition) => {
  switch (position) {
    case 'above-left': {
      return 'below-right';
    }
    case 'above-center': {
      return 'below-center';
    }
    case 'above-right': {
      return 'below-left';
    }
    case 'side-left': {
      return 'side-right';
    }
    case 'side-right': {
      return 'side-left';
    }
    case 'below-left': {
      return 'above-right';
    }
    case 'below-center': {
      return 'above-center';
    }
    case 'below-right': {
      return 'above-left';
    }
    default:
      return 'Unreachable!' as never;
  }
};

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  classes: classOverrides,
  className,
  isOpen,
  position = 'above-center',
  theme,
  ...props
}) => {
  const classes = mergeClasses(useTooltipStyles({ position, theme, children }), classOverrides);
  const { base, tooltip } = children;
  const caretPosition = tooltipPositionToCaretPosition(position);

  return (
    <div className={classes.root} data-analytics="tooltip" {...props}>
      {base}
      {isOpen && (
        <CaretCard theme={theme} classes={{ root: classes.tooltip }} caretPosition={caretPosition}>
          {tooltip}
        </CaretCard>
      )}
    </div>
  );
};
